import { useAppSelector, useAppDispatch } from "@/services/hooks";
import React, { useEffect, useState } from "react";

import { COUNTRY_OPTIONS_NAME_ONLY } from "@/utils/selectOptions";
import NotificationAlertOptions from "@/layouts/Alerts";
import { markInvalidFields } from "@/validation/markInvalidFields";
import {
  addClient,
  restoreClient,
  releaseClient,
} from "@/services/clientService/clientActions";
import { useImageUpload } from "@/services/ImageServices/useImageUpload";
import type { PickedImage } from "../User/adding-user/ImageUploadContainer";
import AddNewClientView from "./AddNewClientView";
import { makeClientOnChange, showClientMessage } from "./clientFormHandlers";
import { asCaught } from "@/services/caughtError";

const AddNewClientContainer = () => {
  const dispatch = useAppDispatch();
  const uploadImage = useImageUpload();

  // Redux state
  const errors = useAppSelector((s) => s.errors);
  const clients = useAppSelector((s) => s.clients);

  // Local state
  const [loading, setLoading] = useState(false);
  const [clientImg, setClientImg] = useState<PickedImage>({});
  // SCRUM-1818: the soft-deleted client a 409 named, plus the payload that got
  // the 409 — Start fresh re-sends exactly that (logo already uploaded).
  const [deletedHolder, setDeletedHolder] = useState<{
    id: string;
    msg: string;
    value: string;
    payload: any;
  } | null>(null);
  const [client, setClient] = useState({
    name: "",
    about: "",
    contacts: {
      email: "",
      number: "",
      website: "",
    },
    social_links: {
      facebook: "",
      linkedin: "",
      twitter: "",
    },
    adresse: "",
    country_code: "",
    legal_name: "",
  });

  // mêmes flags de validation que dans le composant initial
  const [validation, setValidation] = useState({
    name: true,
    country_code: true,
  });

  // === Effets équivalents à UNSAFE_componentWillReceiveProps ===

  // Erreurs serveur (ex: champs vides renvoyés par backend)
  // Server errors: stop the spinner and mark the invalid fields. Adjusting state while rendering
  // (React: "storing information from previous renders") instead of an effect that only
  // copies the store value into state (S9 mirror-effect, SCRUM-2254).
  const [seenErrors, setSeenErrors] = useState<typeof errors | null>(null);
  if (errors !== seenErrors) {
    setSeenErrors(errors);
    if (errors?.errors) {
      setValidation(markInvalidFields(validation, errors.errors));
      setLoading(false);
    }
  }

  // Succès ajout client
  useEffect(() => {
    showClientMessage(clients?.message, dispatch, setLoading, setClient);
  }, [clients?.message, dispatch]);

  // === Handlers ===

  const ImageUploadRecievedHandler = (img) => {
    setClientImg(img);
  };

  const onChange = makeClientOnChange(setClient, setValidation);

  const onChangeCountryCode = (option) => {
    setClient((prev) => ({ ...prev, country_code: option?.value || "" }));
    setValidation((prev) => ({ ...prev, country_code: true }));
  };

  // addClient rejects to the central hub on every failure (which toasts it);
  // the one rejection this form acts on is the SCRUM-1818 409, whose body
  // names the deleted client that still holds the name.
  const submitClient = async (payload) => {
    setDeletedHolder(null);
    try {
      await dispatch(addClient(payload)).unwrap();
    } catch (err) {
      // The rejected thunk's payload: the server's `{ errors: [...] }` body.
      const first = (err as { errors?: any[] })?.errors?.[0];
      if (first?.deletedId) {
        setDeletedHolder({
          id: String(first.deletedId),
          msg: String(first.msg ?? "A deleted client holds this name"),
          value: String(first.value ?? ""),
          payload,
        });
      }
    }
  };

  const onRestore = async () => {
    if (!deletedHolder) return;
    try {
      await dispatch(restoreClient(deletedHolder.id)).unwrap();
    } catch {
      return; // surfaced via the hub; the offer stays on screen
    }
    setDeletedHolder(null);
    setLoading(false);
    NotificationAlertOptions("success", "The deleted client is restored");
  };

  const onStartFresh = async () => {
    if (!deletedHolder) return;
    try {
      await dispatch(releaseClient(deletedHolder.id)).unwrap();
    } catch {
      return; // surfaced via the hub; the offer stays on screen
    }
    await submitClient(deletedHolder.payload);
  };

  const onClickAdd = async (e) => {
    e.preventDefault();

    if (!client.country_code.trim()) {
      setValidation((prev) => ({ ...prev, country_code: false }));
      return;
    }

    // Freshly picked file: upload first, then save inline with the returned URL
    // (useImageUpload clears images.url on success). The two direct-save branches
    // — an already-hosted secure_url, or no image — are unchanged.
    if (clientImg && clientImg.data) {
      const path = `/CLIENTS/${clientImg.imgName}`;
      setLoading(true);
      try {
        const url = await uploadImage(clientImg.data, path);
        await submitClient({ ...client, clientImg: url });
      } catch (err) {
        setLoading(false);
        NotificationAlertOptions(
          "error",
          asCaught(err)?.response?.data?.message ||
            asCaught(err)?.message ||
            "Saving the client failed. Please try again."
        );
      }
    } else if (clientImg && clientImg.secure_url !== undefined) {
      const payload = { ...client, clientImg: clientImg.secure_url };
      await submitClient(payload);
    } else {
      await submitClient(client);
    }
  };

  return (
    <AddNewClientView
      client={client}
      validation={validation}
      updatedCountries={COUNTRY_OPTIONS_NAME_ONLY}
      loading={loading}
      onChange={onChange}
      onChangeCountryCode={onChangeCountryCode}
      onClickAdd={onClickAdd}
      ImageUploadRecievedHandler={ImageUploadRecievedHandler}
      deletedHolder={deletedHolder}
      onRestore={onRestore}
      onStartFresh={onStartFresh}
    />
  );
};

export default AddNewClientContainer;
